import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { appConfig } from '../config/appConfig';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_API_URL;
const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

function userFromToken(token) {
  if (!token) return null;
  const payload = decodeJwt(token);
  if (!payload) return null;
  if (payload.exp && payload.exp * 1000 < Date.now()) return null;
  return {
    id: payload.user_id ?? null,
    email: payload.email || null,
    isAdmin: !!payload.is_admin,
    perms: Array.isArray(payload.perms) ? payload.perms : [],
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const intervalRef = useRef(null);

  const applyToken = useCallback((token) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userFromToken(token));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/auth/me`);
      if (res.data?.token) {
        applyToken(res.data.token);
      }
    } catch (err) {
      // 401 means our token is no longer valid (deactivated, deleted, expired)
      if (err.response?.status === 401) {
        logout();
      }
      // Other errors (network blip): keep current state silently
    }
  }, [applyToken, logout]);

  // Initial load: hydrate from localStorage, then verify with backend
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const u = userFromToken(token);
      // On the PGP frontend, refuse to hydrate a session for someone without
      // the pgp perm. They get bounced back to /login.
      const pgpOk = !appConfig.isPgp || (u && (u.isAdmin || u.perms.includes('pgp')));
      if (u && pgpOk) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(u);
        refresh().finally(() => setIsLoading(false));
        return;
      }
      localStorage.removeItem('token');
    }
    setIsLoading(false);
  }, [refresh]);

  // Refresh on tab focus and on a 5-min interval
  useEffect(() => {
    if (!user) return;

    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    intervalRef.current = setInterval(refresh, REFRESH_INTERVAL_MS);

    return () => {
      window.removeEventListener('focus', onFocus);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user, refresh]);

  const login = useCallback(
    (token) => {
      applyToken(token);
    },
    [applyToken]
  );

  const hasPerm = (perm) => {
    if (!user) return false;
    if (user.isAdmin) return true;
    return user.perms.includes(perm);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        user,
        isLoading,
        login,
        logout,
        hasPerm,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
